import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { Box, Tabs, Tab, useTheme, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ReferenceDetails from "./ReferenceDetails";
import CollateralDetails from "./CollateralDetails";
import { AppDispatch, RootState } from "../../../../services/redux/store";
import { setStatusPopup } from "../../../../services/redux/slices/statusPopupSlice";
import { setSummarySaveInProgress, setSkipNextSummaryRefetch, bumpFormJsonRefreshNonce } from "../../../../services/redux/slices/stepperDynamicFormSlice";
import {
  getCollateralFieldsByGroup,
  getReferenceCollateralValidationErrors,
  splitCoOwnerCollateralSections,
} from "../../../../utils/helpers";





interface ReferenceCollateralDetailsProps {
  fields: any[];
  renderField: (field: any) => React.ReactNode;
  formikRef: any;
  values?: any;
  errors?: Record<string, any>;
  touched?: Record<string, any>;
  isSaving?: boolean;
  activeTab?: number;
  setActiveTab?: (tabIndex: number) => void;
  onReferenceSaveAndNext?: (referenceNumber: number) => Promise<boolean | void>;
  onCollateralSaveAndNext?: (collateralNumber: number) => Promise<boolean | void>;
  pageType?:string;
  summarySaveRequestId?: number;
  onSummarySaveSuccess?: () => Promise<void> | void;
  /** When set in SUMMARY edit, hide tab switcher and show only this section. */
  summaryLockedTab?: 0 | 1;
  isDIYJourney?: boolean;
  /** Passed through to CollateralDetails — see its own prop doc. */
  lastSavedCollateralSnapshotByGroupRef?: React.MutableRefObject<Record<number, string>>;
}

const ReferenceCollateralDetails: React.FC<ReferenceCollateralDetailsProps> = ({
  fields,
  renderField,
  formikRef,
  values,
  isSaving,
  activeTab,
  setActiveTab,
  onReferenceSaveAndNext,
  onCollateralSaveAndNext,
  pageType,
  summarySaveRequestId = 0,
  onSummarySaveSuccess,
  summaryLockedTab,
  isDIYJourney = false,
  lastSavedCollateralSnapshotByGroupRef,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));

  const getNormalized = (name: any) => String(name || "").toLowerCase().replace(/[^a-z0-9]/g, "");

  // Reference: basicReference* or addressReference*
  const referenceFields = fields.filter((f: any) => {
    const n = getNormalized(f?.name);
    return n.startsWith("basicreference") || n.startsWith("addressreference");
  });

  // Collateral: collateral*, coownercollateral*, or the standalone
  // "Has Collateral?" gate field (hasCollateral) the get-data API now sends
  // by itself by default, ahead of any actual collateral1... fields.
  const collateralFields = fields
  .filter((f: any) => {
    const n = getNormalized(f?.name);
    return n.startsWith("collateral") || n.startsWith("coownercollateral") || n === "hascollateral";
  })
  .map(item => {
    if (getNormalized(item?.name) === "hascollateral") {
      return item;
    }
    if(!item.name.startsWith("coOwnerCollateral") && !item.name.endsWith("addressLine2")) {
      item.required = true;
    } else {
      item.required = false;
    }
    return item;          // important: return the object
  });

  const formValues = values || formikRef?.current?.values || {};
  // "Has Collateral?" = No: Collateral 1's field keys are permanent (never
  // deleted — see CollateralDetails.tsx's removeCollateralFields), so
  // group-1 validation checks below can no longer rely on "no field keys
  // exist yet" to know it's irrelevant when No is selected. Every
  // required-field validator for collateral group 1 needs to short-circuit
  // on this instead, or a blank-but-present Collateral Type would keep
  // blocking the SUMMARY save even though the applicant said No.
  const hasCollateralRawAnswer = formValues?.hasCollateral;
  const hasCollateralAnswer = String(
    hasCollateralRawAnswer && typeof hasCollateralRawAnswer === "object"
      ? (hasCollateralRawAnswer as any).value ?? (hasCollateralRawAnswer as any).label
      : hasCollateralRawAnswer ?? "",
  ).trim().toLowerCase();
  const isSummaryLocked =
    pageType === "SUMMARY" && typeof summaryLockedTab === "number";
  const activeTabIndex = isSummaryLocked
    ? summaryLockedTab
    : typeof activeTab === "number"
      ? activeTab
      : 0;
  const [expandedReference, setExpandedReference] = useState<number | false>(1);
  const [expandedCollateral, setExpandedCollateral] = useState<number | false>(1);
  const lastHandledSummarySaveRef = useRef<number>(summarySaveRequestId);

  const isFilled = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (value && typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value ?? "").trim() !== "";
  };

  const getMissingReferenceFields = () => {
    const visibleReferenceFields = referenceFields.filter(
      (f: any) => !f?.isRemoved && f?.type !== "hidden" && !f.name.endsWith("AddressLine2")
      && !f.name.endsWith("Area") && !f.name.endsWith("Locality")
    );

    return visibleReferenceFields.filter(
      (f: any) => !isFilled(formValues?.[f?.name]),
    );
  };

  // Every custom Reference & Collateral rule (required-if, format,
  // cross-reference uniqueness, co-owner percentage total) lives in
  // getReferenceCollateralValidationErrors so it can also be wired into
  // Formik's `validate` prop in DynamicForm.tsx / FormEdit.tsx. That makes
  // Formik include these in the SAME atomic errors object it derives from
  // validationSchema on every validation pass — unlike an imperative
  // setFieldError call, which used to get wiped by validateOnChange
  // revalidating the whole form on every keystroke in ANY field, flickering
  // every other still-invalid field as its error briefly vanished and came
  // back. Recomputed fresh each render from the current form values.
  const customValidationErrors = getReferenceCollateralValidationErrors(fields, formValues);

  const getReferenceFieldsForGroup = (n: number) =>
    referenceFields.filter((f: any) => {
      if (f?.isRemoved || f?.type === "hidden") return false;
      const name = String(f?.name || "");
      return name.startsWith(`basicReference${n}`) || name.startsWith(`addressReference${n}`);
    });

  const REFERENCE_NUMS = [1, 2];

  const filterErrorsByFieldNames = (
    fieldNames: Iterable<any>,
  ): Record<string, string> => {
    const nameSet = new Set(fieldNames);
    const errors: Record<string, string> = {};
    Object.entries(customValidationErrors).forEach(([name, message]) => {
      if (nameSet.has(name)) errors[name] = message;
    });
    return errors;
  };

  const applyFieldErrorsToFormik = (errorsToApply: Record<string, string>) => {
    if (!formikRef?.current) return;
    const touched = { ...(formikRef.current.touched || {}) } as Record<string, boolean>;
    Object.keys(errorsToApply).forEach((name) => {
      touched[name] = true;
    });
    formikRef.current.setTouched(touched, false);
    Object.entries(errorsToApply).forEach(([name, message]) => {
      formikRef.current.setFieldError(name, message);
    });
  };

  const getAllReferenceFormatErrors = (): Record<string, string> =>
    filterErrorsByFieldNames(referenceFields.map((f: any) => f?.name));

  const getReferenceNumbersWithErrors = (
    errorsByFieldName: Record<string, string>,
  ): number[] => {
    const numbers = new Set<number>();
    Object.keys(errorsByFieldName).forEach((name) => {
      const match = String(name || "").match(
        /^(?:basicReference|addressReference)(\d+)/i,
      );
      if (match) numbers.add(Number(match[1]));
    });
    return Array.from(numbers).sort((a, b) => a - b);
  };

  const getReferenceGroupErrors = (fieldsForGroup: any[]): Record<string, string> =>
    filterErrorsByFieldNames(fieldsForGroup.map((f: any) => f?.name));

  const getCollateralGroupErrors = (collateralNumber: number): Record<string, string> => {
    const { base, coOwner } = getCollateralFieldsByGroup(collateralFields, collateralNumber);
    return filterErrorsByFieldNames([...base, ...coOwner].map((f: any) => f?.name));
  };

  const getAllCollateralFormatErrors = (): Record<string, string> =>
    filterErrorsByFieldNames(collateralFields.map((f: any) => f?.name));

  const resolveCollateralTypeValue = (raw: any): string =>
    raw && typeof raw === "object" && "value" in raw
      ? String((raw as any).value ?? "")
      : String(raw ?? "");

  const getCollateralNumbersPresent = (): number[] => {
    const numbers = new Set<number>();
    collateralFields.forEach((f: any) => {
      const match = String(f?.name || "").match(/^collateral(\d+)/i);
      if (match) numbers.add(Number(match[1]));
    });
    // No fallback to [1]: collateralFields now always includes the
    // standalone "hasCollateral" gate field, so collateralFields.length > 0
    // no longer implies an actual collateral1... field set exists.
    return Array.from(numbers).sort((a, b) => a - b);
  };

  // Switching Collateral Type (e.g. Property -> FD) changes which fields are
  // actually required for that section — groupErrors/typeRequiredErrors from
  // a previous Save & Next attempt (computed for the OLD type) no longer
  // apply once the type changes, so blank out every error currently shown
  // for this collateral right away instead of leaving stale/irrelevant
  // errors on screen until the next save attempt recomputes them.
  // useLayoutEffect (not useEffect) so the clear commits before the browser
  // paints — otherwise the stale errors would flash briefly before clearing.
  const previousCollateralTypeValuesRef = useRef<Record<number, string>>({});
  useLayoutEffect(() => {
    if (!formikRef?.current) return;

    const collateralNumbers = new Set<number>();
    collateralFields.forEach((f: any) => {
      const match = String(f?.name || "").match(/^collateral(\d+)/i);
      if (match) collateralNumbers.add(Number(match[1]));
    });

    collateralNumbers.forEach((collateralNumber) => {
      const { base, coOwner } = getCollateralFieldsByGroup(collateralFields, collateralNumber);
      const typeField = base.find((f: any) => /Type$/i.test(String(f?.name || "")));
      if (!typeField?.name) return;

      const currentValue = resolveCollateralTypeValue(formValues?.[typeField.name]);
      const previousValue = previousCollateralTypeValuesRef.current[collateralNumber];
      previousCollateralTypeValuesRef.current[collateralNumber] = currentValue;

      if (previousValue === undefined || previousValue === currentValue) return;

      // Blank out groupErrors and typeRequiredErrors for this collateral:
      // every field name either source could have set an error on.
      const staleErrorFieldNames = new Set([
        ...Object.keys(getCollateralGroupErrors(collateralNumber)),
        ...Object.keys(getCollateralTypeRequiredFieldErrors(collateralNumber)),
        ...[...base, ...coOwner].map((f: any) => f?.name),
      ]);
      const currentFormikErrors = formikRef.current.errors || {};
      staleErrorFieldNames.forEach((name) => {
        if (name && currentFormikErrors[name] !== undefined) {
          formikRef.current.setFieldError(name, undefined);
        }
      });
    });
  }, [formValues, collateralFields]);

  const handleTabChange = (_e: React.SyntheticEvent, val: number) => {
    if (isSummaryLocked) return;

    const isMovingToCollateral = activeTab === 0 && val === 1;

    if (!isMovingToCollateral) {
      setActiveTab?.(val);
      return;
    }

    const missingReferenceFields = getMissingReferenceFields();
    const formatErrors = getAllReferenceFormatErrors();
    const hasFormatErrors = Object.keys(formatErrors).length > 0;

    if (missingReferenceFields.length > 0 || hasFormatErrors) {
      if (formikRef?.current?.setTouched) {
        const touched = {
          ...(formikRef?.current?.touched || {}),
        } as Record<string, boolean>;

        missingReferenceFields.forEach((f: any) => {
          if (f?.name) touched[f.name] = true;
        });
        Object.keys(formatErrors).forEach((name) => {
          touched[name] = true;
        });

        formikRef.current.setTouched(touched, true);
      }
      if (hasFormatErrors) {
        applyFieldErrorsToFormik(formatErrors);
      } else {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "Please complete all Reference Details fields before moving to Collateral Details.",
          }),
        );
      }
      return;
    }

    setActiveTab?.(val);
  };

  useEffect(() => {
    // In SUMMARY edit mode, after saving Reference/Collateral we need an immediate Redux refresh
    // so ApplicationDetails UI reflects latest data without full page refresh.
    const refreshReduxFromUserApplicationFetch = async () => {
      try {
        // Force ApplicationDetails to rebuild its summary view.
        // bumpFormJsonRefreshNonce is handled inside FormEdit's summary refresh flow,
        // but Reference/Collateral summary uses ReferenceCollateralDetails.
        // So we trigger both the nonce bump and summary saveInProgress reset.
        dispatch(bumpFormJsonRefreshNonce());
        dispatch(setSummarySaveInProgress(false));
      } catch (e) {
        // no-op
      }
    };

    const isSummaryMode = pageType === "SUMMARY";
    if (!isSummaryMode) return;

    if (summarySaveRequestId === lastHandledSummarySaveRef.current) return;
    lastHandledSummarySaveRef.current = summarySaveRequestId;

    (async () => {
      if (activeTabIndex === 0) {
        // SUMMARY saves every reference (not just the one the accordion happens
        // to have expanded), so switching tabs never leaves a reference unsaved.
        const groupErrors = getAllReferenceFormatErrors();
        if (Object.keys(groupErrors).length > 0) {
          applyFieldErrorsToFormik(groupErrors);
          const referenceNumbersWithErrors =
            getReferenceNumbersWithErrors(groupErrors);
          if (referenceNumbersWithErrors.length > 0) {
            const expandedReferenceHasErrors =
              typeof expandedReference === "number" &&
              referenceNumbersWithErrors.includes(expandedReference);
            if (!expandedReferenceHasErrors) {
              setExpandedReference(referenceNumbersWithErrors[0]);
            }
          }
          // Validation blocked the save — unblock the Save button without
          // triggering ApplicationDetails' "save just finished" refetch,
          // which would otherwise tear down this section before the user
          // can see the inline errors.
          dispatch(setSkipNextSummaryRefetch(true));
          dispatch(setSummarySaveInProgress(false));
          return;
        }

        let anyReferenceSaved = false;
        for (const referenceNumber of REFERENCE_NUMS) {
          const ok = await onReferenceSaveAndNext?.(referenceNumber);
          if (ok !== false) anyReferenceSaved = true;
        }

      if (anyReferenceSaved) {
        await onSummarySaveSuccess?.();
        // ensure UI updates after Reference save
        await refreshReduxFromUserApplicationFetch();
      } else {
        // Nothing was actually saved (blocked/deduped) — don't reload for no reason.
        dispatch(setSkipNextSummaryRefetch(true));
        dispatch(setSummarySaveInProgress(false));
      }
      return;
    }

    // Validate every collateral section up front — both the general format
    // errors (getAllCollateralFormatErrors) and the per-section, type-aware
    // required-field check (getCollateralTypeRequiredFieldErrors, same one
    // validatedOnCollateralSaveAndNext uses) — and only then, if every
    // section passes, call the create/save API. Previously this only
    // validated/saved whichever collateral happened to be expanded in the
    // accordion, so an unvalidated, unsaved Collateral 2+ could silently
    // never reach the create API at all.
    const collateralNumbersPresent = getCollateralNumbersPresent();
    // "Has Collateral?" = No, no collateral1... fields exist to loop over —
    // Save must still persist that answer (onCollateralSaveAndNext already
    // handles the empty-group "No" case), instead of silently doing nothing.
    // "Yes" without any collateral1... fields yet (Add More not used) is left
    // alone here — nothing to save until a real group exists and passes the
    // required-field validation below.
    const collateralNumbers =
      collateralNumbersPresent.length === 0 && hasCollateralAnswer === "no"
        ? [1]
        : collateralNumbersPresent;
    const allCollateralErrors: Record<string, string> = {
      ...getAllCollateralFormatErrors(),
    };
    let collateralNumberMissingPercentage: number | undefined;
    collateralNumbers.forEach((n) => {
      Object.assign(allCollateralErrors, getCollateralTypeRequiredFieldErrors(n));
      const coOwnerRequiredErrors = getCollateralCoOwnerRequiredFieldErrors(n);
      Object.assign(allCollateralErrors, coOwnerRequiredErrors);
      if (
        collateralNumberMissingPercentage === undefined &&
        Object.keys(coOwnerRequiredErrors).some((name) => /OwnershipPercentage$/i.test(name))
      ) {
        collateralNumberMissingPercentage = n;
      }
    });
    if (Object.keys(allCollateralErrors).length > 0) {
      applyFieldErrorsToFormik(allCollateralErrors);
      if (collateralNumberMissingPercentage !== undefined) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: `Please enter co-owner ownership percentage for collateral ${collateralNumberMissingPercentage}`,
          }),
        );
      }
      dispatch(setSkipNextSummaryRefetch(true));
      dispatch(setSummarySaveInProgress(false));
      return;
    }

    // Same co-owner ownership percentage check validatedOnCollateralSaveAndNext
    // runs for a single collateral — applied here across every collateral
    // section before saving any of them, so a percentage total over 100%
    // on Collateral 2+ blocks the SUMMARY save too, not just the single
    // accordion Save & Next.
    const collateralNumberOverLimit = collateralNumbers.find(
      (n) => !isOwnershipPercentageValid(formValues, n),
    );
    if (collateralNumberOverLimit !== undefined) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: `Total ownership cannot exceed 100% for collateral ${collateralNumberOverLimit}. Please check and re-enter the details.`,
        }),
      );
      dispatch(setSkipNextSummaryRefetch(true));
      dispatch(setSummarySaveInProgress(false));
      return;
    }

    let anyCollateralSaved = false;
    for (const collateralNumber of collateralNumbers) {
      const ok = await onCollateralSaveAndNext?.(collateralNumber);
      if (ok !== false) anyCollateralSaved = true;
    }

    if (anyCollateralSaved) {
      await onSummarySaveSuccess?.();
      // ensure UI updates after Collateral save
      await refreshReduxFromUserApplicationFetch();
    } else {
      dispatch(setSkipNextSummaryRefetch(true));
      dispatch(setSummarySaveInProgress(false));
    }
  })();

  }, [
    pageType,
    summarySaveRequestId,
    activeTabIndex,
    expandedReference,
    expandedCollateral,
    onReferenceSaveAndNext,
    onCollateralSaveAndNext,
    onSummarySaveSuccess,
  ]);


  const validatedOnReferenceSaveAndNext = async (referenceNumber: number) => {
    const groupErrors = getReferenceGroupErrors(
      getReferenceFieldsForGroup(referenceNumber),
    );
    if (Object.keys(groupErrors).length > 0) {
      applyFieldErrorsToFormik(groupErrors);
      return false;
    }
    return onReferenceSaveAndNext?.(referenceNumber);
  };

  function isOwnershipPercentageValid(data: Record<string, any>,collateralNumber:number|string): boolean {
  // Collect all co-owner ownership percentages that have a value
  const percentages = Object.keys(data)
    // (?!\d) keeps collateral 1 from also matching collateral 10, 11, ...
    .filter(key => key.endsWith("OwnershipPercentage") && new RegExp(`^coOwnerCollateral${collateralNumber}(?!\\d)`).test(key))
    .map(key => Number(data[key]) || 0)
    .filter(value => value > 0);


  // If no co-owner percentages are filled → valid
  if (percentages.length === 0) return true;

  // Sum of 1 or more percentages should not exceed 100
  const total = percentages.reduce((sum, val) => sum + val, 0);

  return total <= 100;
}

  // Validates the fields required for ONE collateral section based on
  // whichever Collateral Type is selected for it (FD / Savings / Property),
  // and returns a field-name -> message map for any that are blank. Added
  // as a standalone check (called below, alongside the existing
  // getCollateralGroupErrors) specifically so Collateral 2 onwards gets the
  // same per-field "X is required" errors Collateral 1 already shows,
  // instead of silently passing validation and only failing later via the
  // generic "Please complete all required Collateral fields" popup.
  const getCollateralTypeRequiredFieldErrors = (
    collateralNumber: number,
  ): Record<string, string> => {
    const errors: Record<string, string> = {};
    // Collateral 1's field keys are permanent (see ReferenceCollateralDetails'
    // hasCollateralAnswer comment above) — once "No" is selected they stay
    // present but blank, so this must explicitly bail out here instead of
    // relying on "no field keys exist" to mean "nothing to validate".
    if (collateralNumber === 1 && hasCollateralAnswer === "no") return errors;
    const { base } = getCollateralFieldsByGroup(collateralFields, collateralNumber);
    if (!base.length) return errors;

    const resolveValue = (raw: any): string => {
      if (raw && typeof raw === "object") {
        if ("value" in raw) return String((raw as any).value ?? "").trim();
        if ("label" in raw) return String((raw as any).label ?? "").trim();
      }
      return String(raw ?? "").trim();
    };

    const isEmpty = (name: string) => !resolveValue(formValues?.[name]);

    const findField = (pattern: RegExp) =>
      base.find((f: any) => pattern.test(String(f?.name || "")));

    const requireField = (pattern: RegExp, label: string) => {
      const field = findField(pattern);
      if (field?.name && isEmpty(field.name)) {
        errors[field.name] = `${field.label || label} is required`;
      }
    };

    const typeField = findField(/Type$/i);
    if (!typeField?.name) return errors;

    if (isEmpty(typeField.name)) {
      errors[typeField.name] = `${typeField.label || "Collateral Type"} is required`;
      return errors;
    }

    const rawTypeValue = formValues?.[typeField.name];
    // The Type dropdown stores a numeric code (307/308/309) once picked, not
    // the text label — check both so mode detection doesn't silently fail
    // as soon as a value is selected.
    const codeValue =
      rawTypeValue && typeof rawTypeValue === "object" && "value" in rawTypeValue
        ? String((rawTypeValue as any).value ?? "").trim()
        : String(rawTypeValue ?? "").trim();
    const resolvedTypeValue = resolveValue(rawTypeValue).toLowerCase();

    const isFixed = codeValue === "307" || /fixed\s*deposit|^fd$/.test(resolvedTypeValue);
    const isSavings = codeValue === "309" || /saving/.test(resolvedTypeValue);
    const isProperty = codeValue === "308" || /property/.test(resolvedTypeValue);
    if (isFixed) {
      // FD-specific field names ("collateral2FdHolderName"/"...FdNumber") —
      // matched exactly so a Savings collateral's own "HolderName"/
      // "AccountNumber" fields (which the same base list also carries,
      // since Collateral 2 clones every field variant from the template)
      // are never mistaken for these.
      requireField(/Value$/i, "Collateral Value");
      requireField(/FdHolderName$/i, "Name FD Holder");
      requireField(/FdNumber$/i, "FD Number");
    } else if (isSavings) {
      // Savings-specific field names — "HolderName" but NOT "FdHolderName".
      requireField(/Value$/i, "Collateral Value");
      requireField(/(?<!Fd)HolderName$/i, "Name Holder");
      requireField(/AccountNumber$/i, "Account Number");
    } else if (isProperty) {
      requireField(/(?<!Total)Value$/i, "Collateral Value");
      requireField(/UnitPrice$/i, "Per Price Unit");
      requireField(/NoOfUnits$/i, "Number of Units");
      requireField(/TotalValue$/i, "Total Value");
      requireField(/PropertypeName$/i, "Property Type");
      requireField(/PropertySubTypeName$/i, "Property Sub Type");
      requireField(/AddressType$/i, "Address Type");
      requireField(/AddressLine1$/i, "Address Line 1");
      // Address Line 2 is always optional.
      requireField(/Pincode$/i, "Pincode");
      requireField(/City$/i, "City");
      requireField(/State$/i, "State");
      requireField(/Country$/i, "Country");
    }

    return errors;
  };

  // If a Co-Owner section's Name has been filled in, its Ownership
  // Percentage and Phone Number become required too — checked per
  // co-owner SECTION (not just the first one found across the whole
  // collateral) and for every collateral number (1 onwards), not only
  // Collateral 1. Added as its own standalone check — existing checks
  // (getCollateralGroupErrors / getCollateralTypeRequiredFieldErrors) are
  // untouched.
  const getCollateralCoOwnerRequiredFieldErrors = (
    collateralNumber: number,
  ): Record<string, string> => {
    const errors: Record<string, string> = {};
    // See getCollateralTypeRequiredFieldErrors above — Collateral 1's field
    // keys (co-owner included) persist blank after removal, so "No" needs
    // an explicit bail-out here too.
    if (collateralNumber === 1 && hasCollateralAnswer === "no") return errors;
    const { coOwner } = getCollateralFieldsByGroup(collateralFields, collateralNumber);
    // A co-owner just added via Add More (SUMMARY especially) lives in Formik
    // values before its field config reaches `fields`, so pick those up from
    // the form values too — otherwise its blank percentage is never flagged.
    const knownCoOwnerNames = new Set(coOwner.map((f: any) => String(f?.name || "")));
    const coOwnerKeyRegex = new RegExp(
      `^coOwnerCollateral${collateralNumber}(?:_\\d+)?(Name|OwnershipPercentage|PhoneNumber)$`,
      "i",
    );
    const coOwnerLabels: Record<string, string> = {
      name: "Name",
      ownershippercentage: "Ownership Percentage",
      phonenumber: "Phone Number",
    };
    const coOwnerFieldsFromValues = Object.keys(formValues || {})
      .filter(
        (key) =>
          coOwnerKeyRegex.test(key) &&
          !knownCoOwnerNames.has(key) &&
          formValues?.[key] !== undefined,
      )
      .map((key) => ({
        name: key,
        label: coOwnerLabels[String(key.match(coOwnerKeyRegex)?.[1] || "").toLowerCase()],
      }));
    const sections = splitCoOwnerCollateralSections([
      ...coOwner,
      ...coOwnerFieldsFromValues,
    ]).filter(
      (section) => section.length > 0,
    );

    const resolveValue = (raw: any): string => {
      if (raw && typeof raw === "object") {
        if ("value" in raw) return String((raw as any).value ?? "").trim();
        if ("label" in raw) return String((raw as any).label ?? "").trim();
      }
      return String(raw ?? "").trim();
    };
    const isEmpty = (name: string) => !resolveValue(formValues?.[name]);

    sections.forEach((section: any[]) => {
      const nameField = section.find((f: any) => {
        const n = String(f?.name || "").toLowerCase();
        return (
          n.includes("name") &&
          !n.includes("percentage") &&
          !n.includes("phone") &&
          !n.includes("mobile")
        );
      });
      if (!nameField?.name || isEmpty(nameField.name)) return;

      const percentageField = section.find((f: any) =>
        /(Ownership)?Percentage$/i.test(String(f?.name || "")),
      );
      if (percentageField?.name && isEmpty(percentageField.name)) {
        errors[percentageField.name] =
          `${percentageField.label || "Ownership Percentage"} is required`;
      }

      const phoneField = section.find((f: any) =>
        /(PhoneNumber|Mobile)$/i.test(String(f?.name || "")),
      );
      if (phoneField?.name && isEmpty(phoneField.name)) {
        errors[phoneField.name] = `${phoneField.label || "Phone Number"} is required`;
      }
    });

    return errors;
  };

  const validatedOnCollateralSaveAndNext = async (collateralNumber: number) => {
    const groupErrors = getCollateralGroupErrors(collateralNumber);
    // Co-owner name -> percentage/phone required check, for every
    // collateral number (1 onwards) — added alongside the existing checks
    // below without altering them.
    const coOwnerRequiredErrors = getCollateralCoOwnerRequiredFieldErrors(collateralNumber);


    if(collateralNumber>1){
      const typeRequiredErrors = getCollateralTypeRequiredFieldErrors(collateralNumber);
      const allCollateralErrors = { ...groupErrors, ...typeRequiredErrors, ...coOwnerRequiredErrors };
      if (Object.keys(allCollateralErrors).length > 0) {
        applyFieldErrorsToFormik(allCollateralErrors);
        return false;
      }
    }else{
        const allCollateralErrors = { ...groupErrors, ...coOwnerRequiredErrors };
        if (Object.keys(allCollateralErrors).length > 0) {
          applyFieldErrorsToFormik(allCollateralErrors);
          return false;
        }
    }
    
    if (!isOwnershipPercentageValid(formValues,collateralNumber)) {
       dispatch(
          setStatusPopup({
            status: "error",
            message: "Total ownership cannot exceed 100%. Please check and re-enter the details.",
          }),
        );
      // Was `return;` (resolves to undefined) — CollateralDetails' Save &
      // Next handler only treats `result === false` as "blocked"; undefined
      // fell through and advanced/collapsed the accordion to the next
      // collateral even though this one still had an error.
      return false;
    }
    return onCollateralSaveAndNext?.(collateralNumber);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {!isSummaryLocked && (
        <Box
          sx={{
            borderColor: "divider",
            mb: 2,
            px: isXsOrSm ? 0 : 2,
            mt: { xs: 3, sm : 2 },
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isXsOrSm ? "fullWidth" : "standard"}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontFamily: "Jost, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                color: "#4B465C",
                ml: { xs: 2, sm: 0 },
                mr: { xs: 2, sm: 0 },
              },
              "& .Mui-selected": {
                color: "#33c2c4 !important",
                fontWeight: 600,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#33c2c4",
              },
            }}
          >
            <Tab label="Reference Details" />
            <Tab label="Collateral Details" />
          </Tabs>
        </Box>
      )}

      {/* Tab Panels */}
      {activeTabIndex === 0 && (
        <ReferenceDetails
          fields={referenceFields}
          renderField={renderField}
          formikRef={formikRef}
          values={values}
          isSaving={isSaving}
          onSaveAndNext={validatedOnReferenceSaveAndNext}
          pageType={pageType}
          expandedReference={expandedReference}
          onExpandedChange={setExpandedReference}
          hideInternalSaveButton={pageType === "SUMMARY"}
        />
      )}

      {activeTabIndex === 1 && (
        <CollateralDetails
          fields={collateralFields}
          renderField={renderField}
          formikRef={formikRef}
          values={values}
          isSaving={isSaving}
          onSaveAndNext={validatedOnCollateralSaveAndNext}
          expandedCollateral={expandedCollateral}
          onExpandedChange={setExpandedCollateral}
          hideInternalSaveButton={pageType === "SUMMARY"}
          pageType={pageType}
          isDIYJourney={isDIYJourney}
          lastSavedCollateralSnapshotByGroupRef={lastSavedCollateralSnapshotByGroupRef}
        />
      )}
    </Box>
  );
};

export default ReferenceCollateralDetails;
